import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)] text-white">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-50 bg-[hsl(224,71%,4%)]/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/admin-access" className="flex items-center gap-3">
              <div className="p-2 bg-primary rounded-lg">
                <Shield className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">ServSpect</span>
            </Link>
            <Button variant="ghost" asChild className="text-white hover:bg-white/10">
              <Link to="/admin-access">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-16 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 text-primary">Terms of Service</h1>
        <p className="text-white/60 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <div className="space-y-8 text-white/80">
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">1. Acceptance of Terms</h2>
            <p className="mb-4">
              By accessing or using ServSpect's server monitoring platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
              If you disagree with any part of these terms, you may not access the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">2. Description of Service</h2>
            <p className="mb-4">
              ServSpect provides a server monitoring and security platform that includes:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Real-time performance metrics collection (CPU, memory, disk, network)</li>
              <li>Windows event log aggregation and analysis</li>
              <li>Security event monitoring and alerting</li>
              <li>Agent-based data collection for Windows servers</li>
              <li>Dashboard visualization and reporting</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">3. User Accounts</h2>
            <p className="mb-4">
              When you create an account with us, you must provide accurate, complete, and current information. 
              You are responsible for safeguarding your account credentials and for any activities or actions under your account.
            </p>
            <p>
              You agree to notify us immediately of any unauthorized access to or use of your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">4. Acceptable Use</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Use the Service for any unlawful purpose or in violation of any laws</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Interfere with or disrupt the integrity or performance of the Service</li>
              <li>Transmit any viruses, malware, or other malicious code</li>
              <li>Collect or harvest any information from other users without consent</li>
              <li>Use the Service to monitor systems you do not have authorization to monitor</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">5. Data Collection and Privacy</h2>
            <p className="mb-4">
              Our collection and use of data is governed by our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>. 
              By using the Service, you consent to such data collection and processing.
            </p>
            <p>
              You are responsible for ensuring that you have all necessary rights and consents to collect and transmit 
              server data from your monitored systems to our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">6. Service Availability</h2>
            <p className="mb-4">
              We strive to maintain high availability but do not guarantee uninterrupted access to the Service. 
              We may temporarily suspend access for maintenance, updates, or other operational reasons.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">7. Intellectual Property</h2>
            <p className="mb-4">
              The Service and its original content, features, and functionality are owned by ServSpect and are protected 
              by international copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">8. Limitation of Liability</h2>
            <p className="mb-4">
              To the maximum extent permitted by law, ServSpect shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages, including loss of data, profits, or business opportunities, 
              arising from your use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">9. Termination</h2>
            <p className="mb-4">
              We may terminate or suspend your account immediately, without prior notice, for any reason, 
              including breach of these Terms. Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">10. Changes to Terms</h2>
            <p className="mb-4">
              We reserve the right to modify or replace these Terms at any time. Material changes will be notified 
              via email or through the Service. Your continued use after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-white">11. Contact Us</h2>
            <p>
              If you have questions about these Terms, please contact us at{" "}
              <a href="mailto:legal@servspect.com" className="text-primary hover:underline">
                legal@servspect.com
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 bg-[hsl(224,71%,4%)]">
        <div className="container mx-auto px-4 text-center text-white/60 text-sm">
          <p>© {new Date().getFullYear()} ServSpect. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
